import {
    Component,
    EventEmitter,
    Input,
    Output,
    SimpleChanges
} from '@angular/core';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    BatterySetDto,
    BatterySetService,
    CurrentPersonDto,
    DO_NOTHING,
    FEATURE_ORGANISATION_RELATIONSHIPS,
    FlyFreelyError,
    FlyFreelyLoggingService,
    hasFeatureFlag,
    PersonsOrganisationDto,
    TransferResourceCommand,
    UserService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { BatterySetDetails } from '../battery-set-details.component';

@Component({
    selector: 'battery-set-view',
    templateUrl: './battery-set-view.component.html'
})
export class BatterySetView {
    @Input() currentOrganisation: PersonsOrganisationDto;
    @Input() batterySet: BatterySetDto;
    @Input() modal: BsModalRef<BatterySetDetails>;
    @Output() edit = new EventEmitter<void>();
    @Output() deleted = new EventEmitter<void>();
    @Output() closeModal = new EventEmitter<void>();

    hasEdit = false;
    hasDelete = false;
    canEdit = true;
    canDelete?: boolean;
    isOwner?: boolean;
    isSystemAdmin = false;
    hasCustodian = false;

    private workTracker = new WorkTracker();
    working = false;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private batterySetService: BatterySetService,
        private commonDialoguesService: CommonDialoguesService,
        private logging: FlyFreelyLoggingService,
        private userService: UserService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('batterySet' in changes || 'currentOrganisation' in changes) {
            this.refreshPermissions();
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refreshPermissions() {
        this.hasEdit = this.batterySet.availableActions.hasEdit;
        this.hasDelete = this.batterySet.availableActions.hasDelete;
        this.canEdit = this.batterySet.availableActions.canEdit;
        this.canDelete = this.batterySet.availableActions.canDelete;
        this.isOwner =
            this.batterySet.organisationId === this.currentOrganisation.id;
        this.hasCustodian = hasFeatureFlag(
            this.currentOrganisation,
            FEATURE_ORGANISATION_RELATIONSHIPS
        );
        this.isSystemAdmin =
            this.userService.getCurrentUser().type ===
            CurrentPersonDto.Type.SYSTEM_ADMIN;
    }

    onEdit() {
        this.edit.emit();
    }

    openTransferDialogue() {
        this.commonDialoguesService
            .showFormlyDialogue(
                'Transfer to another organisation',
                'Transfer',
                true,
                true,
                [
                    {
                        type: 'info',
                        props: {
                            message:
                                'Enter the organisation ID of the target organisation below. The battery set will be transferred to that organisation. This will unshare to any child organisations and transfer all maintenance to the new organisation.'
                        }
                    },
                    {
                        key: 'targetOrganisationId',
                        type: 'input',
                        props: {
                            type: 'number',
                            label: 'Target Organisation ID',
                            required: true
                        }
                    }
                ],
                {},
                (data: TransferResourceCommand) =>
                    firstValueFrom(
                        this.batterySetService.transfer(
                            this.batterySet.id,
                            data
                        )
                    )
            )
            .then(result => {
                this.logging.success('Battery set transfer successful');
                this.closeModal.emit();
            })
            .catch(DO_NOTHING);
    }

    delete() {
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Delete Battery',
                'Are you sure you wish to delete this battery set?',
                'Delete',
                () =>
                    firstValueFrom(
                        this.batterySetService.delete(this.batterySet.id).pipe(
                            tap({
                                error: (error: FlyFreelyError) =>
                                    this.logging.error(
                                        error,
                                        `Error deleting battery set: ${error.message}`
                                    )
                            })
                        )
                    )
            )
            .then(battery => {
                this.logging.success(`Battery set has been deleted`);
                this.deleted.next();
                doneWorking();
            })
            .catch(() => doneWorking());
    }
}
